// Адмін: оцінки пояснень. Показує, де юзери ставлять 👎 і що пишуть —
// щоб адресно виправляти слабкі пояснення (їх 2332, вручну не переглянеш).
import { useState } from 'react';
import { api } from '../../lib/api';
import { useFetch } from '../../lib/useFetch';
import { Loader } from '../../components/Loader';
import { ErrorView } from '../../components/ErrorView';

export function AdminExplanations() {
  const [sort, setSort] = useState<'problems' | 'likes'>('problems');
  const list = useFetch(() => api.admin.explanations(sort), [sort]);

  return (
    <div className="space-y-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 flex-1 text-xs text-tg-hint">
          {sort === 'problems'
            ? 'Пояснення з мінусами — кандидати на переписування.'
            : 'Найкорисніші пояснення за оцінками юзерів.'}
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            onClick={() => setSort((s) => (s === 'problems' ? 'likes' : 'problems'))}
            className="rounded-lg bg-tg-secondary-bg px-3 py-1.5 text-xs"
          >
            {sort === 'problems' ? '👍 Найкращі' : '👎 Проблемні'}
          </button>
          <button onClick={list.reload} className="rounded-lg bg-tg-secondary-bg px-3 py-1.5 text-xs">
            ↻
          </button>
        </div>
      </div>

      {list.loading && <Loader />}
      {list.error && <ErrorView error={list.error} onRetry={list.reload} />}

      {list.data && (
        <>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="Оцінок" value={list.data.totals.total} />
            <Stat label="👍" value={list.data.totals.up} accent="success" />
            <Stat label="👎" value={list.data.totals.down} accent="destructive" />
          </div>

          {list.data.items.length === 0 && (
            <p className="card text-sm text-tg-hint">
              {sort === 'problems' ? 'Скарг поки немає 🎉' : 'Оцінок поки немає.'}
            </p>
          )}

          {list.data.items.map((it) => (
            <article key={`${it.questionId}-${it.style}`} className="card space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs text-tg-hint">
                    #{it.questionId} · {it.categoryName} ·{' '}
                    {it.style === 'casual' ? '😎 дружбан' : '🎓 інструктор'}
                  </div>
                  <div className="mt-0.5 text-sm font-medium">{it.textUk}</div>
                </div>
                <div
                  className={`shrink-0 rounded-lg px-2 py-1 text-xs font-bold ${
                    it.score < 0 ? 'bg-tg-destructive/15 text-tg-destructive' : 'bg-tg-success/15 text-tg-success'
                  }`}
                >
                  {it.score > 0 ? `+${it.score}` : it.score}
                </div>
              </div>

              {it.explanation && (
                <p className="rounded-lg bg-tg-bg p-2 text-xs leading-relaxed text-tg-hint">
                  {it.explanation}
                </p>
              )}

              {it.comments.length > 0 && (
                <div className="space-y-1">
                  {it.comments.map((c, i) => (
                    <div key={i} className="text-[11px] text-tg-destructive">
                      ⚠️ {c}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-3 text-[11px] text-tg-hint">
                <span>оцінок: {it.votes}</span>
                <span>👎 {it.downs}</span>
                <a href={`/admin/questions/${it.questionId}`} className="text-tg-link">
                  Редагувати →
                </a>
              </div>
            </article>
          ))}
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: 'success' | 'destructive';
}) {
  return (
    <div className="card py-2">
      <div
        className={`text-lg font-bold ${
          accent === 'success'
            ? 'text-tg-success'
            : accent === 'destructive'
              ? 'text-tg-destructive'
              : ''
        }`}
      >
        {value}
      </div>
      <div className="text-[11px] text-tg-hint">{label}</div>
    </div>
  );
}
